import { useState, useEffect, useCallback, useMemo, useRef, useReducer } from 'react';
import {
  getParkingTimeline, getParkingSpots, createParkingSpot, updateParkingSpot, deleteParkingSpot,
  createParkingAssignment, updateParkingAssignment, deleteParkingAssignment,
  getProperties, getUnits, getTenants,
} from '@/lib/api';
import { ParkingSpotRow } from '@/components/parking/ParkingSpotRow';
import { ParkingSpotDialog } from '@/components/parking/ParkingSpotDialog';
import { ParkingAssignDialog } from '@/components/parking/ParkingAssignDialog';
import { ParkingSpotsList } from '@/components/parking/ParkingSpotsList';
import { ParkingAssignmentPopup } from '@/components/ParkingAssignmentPopup';
import { TimelineHeader } from '@/components/calendar/TimelineHeader';
import { TodayMarker } from '@/components/calendar/TodayMarker';
import { dateToX, DAY_WIDTH, HEADER_HEIGHT, ROW_HEIGHT, PARKING_LEFT_COL_WIDTH, setResponsiveCalendarDims, subscribeCalendarDims } from '@/components/calendar/calendarConstants';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Car, Plus, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, UserPlus, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { differenceInDays, parseISO, eachMonthOfInterval } from 'date-fns';

const emptySpotForm = { spot_type: 'designated', spot_number: '', location: '', needs_tag: false, tag_info: '', decal_number: '', decal_year: '', notes: '', property_id: '' };
const emptyAssignForm = { parking_spot_id: '', tenant_id: '', tenant_name: '', property_id: '', unit_id: '', start_date: '', end_date: '', notes: '', is_active: true };

export default function ParkingPage() {
  const [timeline, setTimeline] = useState(null);
  const [spots, setSpots] = useState([]);
  const [properties, setProperties] = useState([]);
  const [allUnits, setAllUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const [spotDialog, setSpotDialog] = useState(false);
  const [editingSpot, setEditingSpot] = useState(null);
  const [spotForm, setSpotForm] = useState(emptySpotForm);

  const [assignDialog, setAssignDialog] = useState(false);
  const [editingAssign, setEditingAssign] = useState(null);
  const [assignForm, setAssignForm] = useState(emptyAssignForm);

  const [saving, setSaving] = useState(false);
  const [manageExpanded, setManageExpanded] = useState(false);
  // Property filter — mirrors the Calendar page's selector. `all` shows
  // every spot; a specific id filters the timeline + Manage list.
  const [selectedProperty, setSelectedProperty] = useState('all');

  // Quick-view popup state for clicking assignment bars
  const [popupAssignment, setPopupAssignment] = useState(null);
  const [popupSpotLabel, setPopupSpotLabel] = useState('');
  const [popupTenantName, setPopupTenantName] = useState('');

  const scrollRef = useRef(null);
  const leftColRef = useRef(null);

  // Keep parking timeline in step with the same responsive dim toggle the
  // calendar uses. Without this, opening Parking directly on a mobile
  // viewport (without visiting Calendar first) would leave DAY_WIDTH stuck
  // at desktop values.
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => setResponsiveCalendarDims({ isMobile: mq.matches });
    apply();
    const listener = () => apply();
    if (mq.addEventListener) mq.addEventListener('change', listener);
    else mq.addListener(listener);
    const unsub = subscribeCalendarDims(forceUpdate);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', listener);
      else mq.removeListener(listener);
      unsub();
    };
  }, []);

  // Sync vertical scroll between timeline and left column
  const handleTimelineScroll = () => {
    if (scrollRef.current && leftColRef.current) {
      leftColRef.current.scrollTop = scrollRef.current.scrollTop;
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [tl, sp, props, units, ten] = await Promise.all([
        getParkingTimeline(), getParkingSpots(), getProperties(), getUnits(), getTenants(),
      ]);
      setTimeline(tl);
      setSpots(sp);
      setProperties(props);
      setAllUnits(units);
      setTenants(ten);
    } catch { toast.error('Failed to load parking data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const propMap = useMemo(() => {
    const m = {};
    properties.forEach(p => { m[p.id] = p; });
    return m;
  }, [properties]);

  const unitMap = useMemo(() => {
    const m = {};
    allUnits.forEach(u => { m[u.id] = u; });
    return m;
  }, [allUnits]);

  const spotMap = useMemo(() => {
    const m = {};
    spots.forEach(s => { m[s.id] = s; });
    return m;
  }, [spots]);

  // Apply the property filter to the timeline spots. When `all` is
  // selected we still group the flat list by property so the left
  // column can show property-section headers.
  const filteredSpots = useMemo(() => {
    if (!timeline?.spots) return [];
    if (selectedProperty === 'all') return timeline.spots;
    return timeline.spots.filter((s) => (s.property_id || '') === selectedProperty);
  }, [timeline, selectedProperty]);

  // Group `filteredSpots` by property_id. Preserves the timeline's
  // original spot ordering within each group. Property groups are
  // sorted by property name; unassigned spots go last under
  // "Unassigned" so nothing is hidden.
  const spotGroups = useMemo(() => {
    const byPid = new Map();
    filteredSpots.forEach((s) => {
      const pid = s.property_id || '';
      if (!byPid.has(pid)) {
        byPid.set(pid, { property_id: pid, property_name: s.property_name || (pid ? '' : 'Unassigned'), spots: [] });
      }
      byPid.get(pid).spots.push(s);
    });
    // Ensure "Unassigned" ("") sorts last, otherwise alphabetical.
    return Array.from(byPid.values()).sort((a, b) => {
      if (!a.property_id && b.property_id) return 1;
      if (a.property_id && !b.property_id) return -1;
      return (a.property_name || '').localeCompare(b.property_name || '');
    });
  }, [filteredSpots]);

  const PROPERTY_HEADER_HEIGHT = 32; // must match the header cell height below

  const { rangeStart, rangeEnd, months, totalWidth, today, totalHeight } = useMemo(() => {
    if (!timeline) return { rangeStart: new Date(), rangeEnd: new Date(), months: [], totalWidth: 0, today: new Date(), totalHeight: 0 };
    const rs = timeline.range_start ? parseISO(timeline.range_start) : new Date();
    const re = timeline.range_end ? parseISO(timeline.range_end) : new Date();
    const t = timeline.today ? parseISO(timeline.today) : new Date();
    if (!rs.getTime() || !re.getTime() || !t.getTime()) {
      return { rangeStart: new Date(), rangeEnd: new Date(), months: [], totalWidth: 0, today: new Date(), totalHeight: 0 };
    }
    const ms = eachMonthOfInterval({ start: rs, end: re });
    const tw = differenceInDays(re, rs) * DAY_WIDTH;
    // Height sums each group's own header + its spot rows so the
    // Today marker + row backgrounds align with the label column.
    let th = HEADER_HEIGHT;
    for (const g of spotGroups) {
      th += PROPERTY_HEADER_HEIGHT;
      th += g.spots.length * ROW_HEIGHT;
    }
    return { rangeStart: rs, rangeEnd: re, months: ms, totalWidth: tw, today: t, totalHeight: th };
  }, [timeline, spotGroups]);

  useEffect(() => {
    if (timeline && scrollRef.current) {
      const todayX = dateToX(today, rangeStart);
      scrollRef.current.scrollLeft = Math.max(0, todayX - 200);
    }
  }, [timeline, today, rangeStart]);

  const scrollBy = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 30 * DAY_WIDTH * 3, behavior: 'smooth' });
  };

  // Spot CRUD
  const openCreateSpot = () => { setEditingSpot(null); setSpotForm({ ...emptySpotForm }); setSpotDialog(true); };
  const openEditSpot = (s) => {
    setEditingSpot(s);
    setSpotForm({
      spot_type: s.spot_type || 'designated', spot_number: s.spot_number || '', location: s.location || '',
      needs_tag: s.needs_tag || false, tag_info: s.tag_info || '', decal_number: s.decal_number || '', decal_year: s.decal_year || '', notes: s.notes || '',
      property_id: s.property_id || '',
    });
    setSpotDialog(true);
  };
  const handleSaveSpot = async () => {
    if (spotForm.spot_type === 'designated' && !spotForm.spot_number) { toast.error('Spot number is required'); return; }
    if (spotForm.spot_type === 'marlins_decal' && !spotForm.decal_number) { toast.error('Decal number is required'); return; }
    setSaving(true);
    try {
      if (editingSpot) { await updateParkingSpot(editingSpot.id, spotForm); toast.success('Spot updated'); }
      else { await createParkingSpot(spotForm); toast.success('Spot created'); }
      setSpotDialog(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };
  const handleDeleteSpot = async (id) => {
    if (!window.confirm('Delete this parking spot and all its assignments?')) return;
    try { await deleteParkingSpot(id); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed to delete'); }
  };

  // Assignment CRUD
  const openAssign = (spotId, startDate) => {
    setEditingAssign(null);
    setAssignForm({ ...emptyAssignForm, parking_spot_id: spotId, start_date: startDate || '' });
    setAssignDialog(true);
  };
  const openEditAssign = (a) => {
    setEditingAssign(a);
    setAssignForm({
      parking_spot_id: a.parking_spot_id || '',
      tenant_id: a.tenant_id, tenant_name: a.tenant_name || '',
      property_id: a.property_id || '', unit_id: a.unit_id || '',
      start_date: a.start_date, end_date: a.end_date, notes: a.notes || '', is_active: a.is_active ?? true,
    });
    setAssignDialog(true);
  };
  const handleSaveAssign = async () => {
    if (!assignForm.parking_spot_id || !assignForm.tenant_id || !assignForm.start_date || !assignForm.end_date) {
      toast.error('Please fill all required fields'); return;
    }
    setSaving(true);
    try {
      if (editingAssign) { await updateParkingAssignment(editingAssign.id, assignForm); toast.success('Assignment updated'); }
      else { await createParkingAssignment(assignForm); toast.success('Tenant assigned'); }
      setAssignDialog(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save assignment'); }
    finally { setSaving(false); }
  };
  const handleDeleteAssign = async (a) => {
    if (!window.confirm(`Remove ${a.tenant_name} from this spot?`)) return;
    try { await deleteParkingAssignment(a.id); toast.success('Removed'); fetchData(); } catch { toast.error('Failed to delete'); }
  };

  // Find the spot_id from timeline data for a given assignment (assignments on timeline have parking_spot_id implicitly via parent)
  const handleBarClick = (assignment) => {
    // Find which spot this assignment belongs to
    const parentSpot = timeline?.spots?.find(s => s.assignments.some(a => a.id === assignment.id));
    const spotLabel = parentSpot?.label || 'Parking Spot';
    const tenantName = assignment.tenant_name || '';
    setPopupAssignment({ ...assignment, parking_spot_id: parentSpot?.id || '' });
    setPopupSpotLabel(spotLabel);
    setPopupTenantName(tenantName);
  };

  const handlePopupUpdated = () => {
    setPopupAssignment(null);
    fetchData();
  };

  return (
    <div className="space-y-5" data-testid="parking-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="parking-page-title">Parking</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage parking spots and tenant assignments</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedProperty} onValueChange={setSelectedProperty} data-testid="parking-property-selector">
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="parking-property-selector-trigger">
              <SelectValue placeholder="All Properties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openCreateSpot} data-testid="parking-add-spot-btn">
            <Plus className="h-4 w-4 mr-2" />Add Spot
          </Button>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => scrollBy(-1)} data-testid="parking-scroll-left">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" data-testid="parking-scroll-today"
              onClick={() => { if (scrollRef.current) scrollRef.current.scrollTo({ left: Math.max(0, dateToX(today, rangeStart) - 200), behavior: 'smooth' }); }}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={() => scrollBy(1)} data-testid="parking-scroll-right">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-8 rounded" style={{ backgroundColor: 'hsl(262, 40%, 50%)' }} />
          <span className="text-muted-foreground">Assigned Tenant</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-0.5 rounded" style={{ backgroundColor: 'hsl(0, 74%, 50%)' }} />
          <span className="text-muted-foreground">Today</span>
        </div>
      </div>

      {/* Calendar Timeline */}
      {loading && (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!loading && timeline && timeline.spots.length === 0 && (
        <Card className="border-dashed">
          <div className="flex flex-col items-center justify-center py-16">
            <Car className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-semibold">No parking spots yet</h3>
            <p className="text-sm text-muted-foreground mt-1">Add parking spots to see them on the timeline</p>
            <Button className="mt-4" onClick={openCreateSpot}>Add Spot</Button>
          </div>
        </Card>
      )}

      {!loading && timeline && timeline.spots.length > 0 && filteredSpots.length === 0 && (
        <Card className="border-dashed">
          <div className="flex flex-col items-center justify-center py-10">
            <Building2 className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">No parking spots for this property yet.</p>
            <Button size="sm" className="mt-3" onClick={openCreateSpot}>Add spot</Button>
          </div>
        </Card>
      )}

      {!loading && timeline && filteredSpots.length > 0 && (
        <Card className="border border-border/70" data-testid="parking-timeline-card">
          <div className="flex" style={{ maxHeight: 'calc(100vh - 320px)' }}>
            {/* Fixed left column */}
            <div
              ref={leftColRef}
              className="shrink-0 overflow-hidden border-r bg-card"
              style={{ width: PARKING_LEFT_COL_WIDTH }}
              data-testid="parking-left-column"
            >
              {/* Header cell */}
              <div className="flex items-end pb-2 px-4 border-b bg-card" style={{ height: HEADER_HEIGHT }}>
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Parking Spot</span>
              </div>
              {/* Grouped spot labels — each property gets a slim header row above its spots */}
              {spotGroups.map((g) => (
                <div key={g.property_id || '_unassigned'} data-testid="parking-property-group-label">
                  <div
                    className="flex items-center gap-1.5 px-3 border-b bg-emerald-50/60 text-emerald-900"
                    style={{ height: PROPERTY_HEADER_HEIGHT }}
                    data-testid="parking-property-header-label"
                  >
                    <Building2 className="h-3 w-3 flex-shrink-0" />
                    <span className="text-[11px] font-semibold uppercase tracking-wide truncate">
                      {g.property_name || 'Unassigned'}
                    </span>
                    <span className="ml-auto text-[10px] text-emerald-800/70 tabular-nums">{g.spots.length}</span>
                  </div>
                  {g.spots.map((spot) => {
                    const isDecal = spot.spot_type === 'marlins_decal';
                    return (
                      <div key={spot.id} className="flex items-center gap-2 px-3 border-b bg-card" style={{ height: ROW_HEIGHT }} data-testid="parking-left-spot-label">
                        <Badge variant={isDecal ? 'secondary' : 'default'} className="text-[10px] px-1.5 py-0 flex-shrink-0">
                          {isDecal ? 'Decal' : 'Spot'}
                        </Badge>
                        <span className="text-xs font-medium truncate">{spot.label}</span>
                        {!isDecal && spot.needs_tag && (
                          <Badge variant="outline" className="text-[9px] px-1 py-0 flex-shrink-0">Tag</Badge>
                        )}
                        <Button
                          variant="ghost" size="sm" className="h-6 w-6 p-0 ml-auto flex-shrink-0"
                          onClick={(e) => { e.stopPropagation(); openAssign(spot.id, ''); }}
                          data-testid="parking-spot-assign-btn"
                        >
                          <UserPlus className="h-3 w-3" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {/* Scrollable timeline */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-auto relative"
              onScroll={handleTimelineScroll}
              data-testid="parking-timeline-scroll"
            >
              <TimelineHeader months={months} rangeStart={rangeStart} totalWidth={totalWidth} leftColumnHidden />
              {spotGroups.map((g) => (
                <div key={g.property_id || '_unassigned'} data-testid="parking-property-group-timeline">
                  <div
                    className="border-b bg-emerald-50/60"
                    style={{ height: PROPERTY_HEADER_HEIGHT, width: totalWidth, minWidth: totalWidth }}
                  />
                  {g.spots.map((spot) => (
                    <ParkingSpotRow
                      key={spot.id}
                      spot={spot}
                      rangeStart={rangeStart}
                      rangeEnd={rangeEnd}
                      months={months}
                      totalWidth={totalWidth}
                      onAssignClick={(spotId) => openAssign(spotId, '')}
                      onBarClick={handleBarClick}
                      onRowClick={(spotId, dateStr) => openAssign(spotId, dateStr)}
                      leftColumnHidden
                    />
                  ))}
                </div>
              ))}
              <TodayMarker today={today} rangeStart={rangeStart} totalHeight={totalHeight} />
            </div>
          </div>
        </Card>
      )}

      {/* Manage Spots Section */}
      {!loading && (
        <div className="border rounded-lg overflow-hidden">
          <button
            className="w-full px-4 py-3 flex items-center justify-between bg-muted/30 hover:bg-muted/50 transition-colors"
            onClick={() => setManageExpanded(!manageExpanded)}
            data-testid="parking-manage-toggle"
          >
            <span className="text-sm font-semibold">
              Manage Parking Spots ({selectedProperty === 'all' ? spots.length : spots.filter(s => (s.property_id || '') === selectedProperty).length})
            </span>
            {manageExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {manageExpanded && (
            <div className="p-4">
              <ParkingSpotsList
                spots={selectedProperty === 'all' ? spots : spots.filter(s => (s.property_id || '') === selectedProperty)}
                onEdit={openEditSpot}
                onDelete={handleDeleteSpot}
              />
            </div>
          )}
        </div>
      )}

      {/* Dialogs */}
      <ParkingSpotDialog
        open={spotDialog} onClose={() => setSpotDialog(false)} editing={editingSpot}
        spotForm={spotForm} setSpotForm={setSpotForm} onSave={handleSaveSpot} saving={saving}
        properties={properties}
      />
      <ParkingAssignDialog
        open={assignDialog} onClose={() => setAssignDialog(false)} editing={editingAssign}
        assignForm={assignForm} setAssignForm={setAssignForm} onSave={handleSaveAssign} saving={saving}
        tenants={tenants} properties={properties} unitMap={unitMap} spotMap={spotMap} propMap={propMap}
      />
      <ParkingAssignmentPopup
        open={!!popupAssignment}
        onClose={() => setPopupAssignment(null)}
        assignment={popupAssignment}
        spotLabel={popupSpotLabel}
        tenantName={popupTenantName}
        onUpdated={handlePopupUpdated}
      />
    </div>
  );
}
